import React, { useState, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { Tile, TileContent, TileText } from '@one-thd/sui-atomic-components';
import {
  ConfirmedFilled, Delivery, DeliveryExpress, InStorePickup, Store
} from '@one-thd/sui-icons';
import { ToggleSwitch } from '@thd-olt-component-react/checkbox';
import { StoreSelector } from '../StoreSelector/StoreSelector';
import '../../styles/refinement.scss';

import { StoreSelectorPlaceholder } from '../StoreSelector/StoreSelectorPlaceholder';

const StandardRefinement = ({
  dimension,
  id,
  onChange,
  refinement,
  enableMultiStore,
  toggleSwitch,
  onMultiStoreSelectionChange,
  multiStoreSelection,
  onDisplay,
  multiFacet,
  isRefinementSelected,
  parentID
}) => {

  const { selected } = refinement;

  const { channel } = useContext(ExperienceContext);
  const { storeName, isLocalized } = useStore({ varnish: false });

  const [multiStoreIds, setMultiStoreIds] = useState(multiStoreSelection ? [multiStoreSelection] : []);

  const isMobile = channel === 'mobile';
  const isChecked = (!multiFacet && !!selected) || (isRefinementSelected);

  const showStoreSelector = refinement.label.toUpperCase() === 'PICK UP TODAY' && enableMultiStore;

  const desktopSort = useRef('');

  const isApplianceBundleRef = useRef(
    typeof (window) !== 'undefined' && window?.location?.href?.includes('N-5yc1vZ2fkpfuj')
  );

  const handleSortOrderChange = (sortorder) => {
    desktopSort.current = `${sortorder.output.sortby}:${sortorder.output.sortorder}`;
  };
  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('sortorder.value', handleSortOrderChange);
  }, [desktopSort]);

  const handleOnChange = () => {
    if (onChange) {
      const [sortedby, orderedby] = desktopSort.current.split(':');
      onChange({
        refinement, dimension, onDisplay
      });
      window.LIFE_CYCLE_EVENT_BUS.trigger('sortorder.value', { sortby: sortedby, sortorder: orderedby });
    }
  };

  const handleStoreChange = () => {
    if (onChange) {
      onChange({
        refinement,
        dimension,
        onDisplay
      });
    }
  };

  const multiStoreCallback = ({ stores }) => {
    const idCount = multiStoreIds?.length;
    const checkedMultiStoreIds = stores.filter((store) => store.checked)
      .map((checkedStore) => parseInt(checkedStore.storeId, 10));

    // Only apply changes to mobile web, not affect the desktop
    if (!isMobile && checkedMultiStoreIds?.length < idCount && !selected) {
      handleStoreChange();
    }

    // When open the dropdown at the first time, both "multiStoreIds" and "checkedMultiStoreIds" are empty
    // Should apply the filter even both of them are empty in mobile web
    if (isMobile && checkedMultiStoreIds?.length <= idCount && !selected) {
      handleStoreChange();
    }
    // selected returning null after first store is selcted in dropdown
    // we are adding additional check to see if there are unchecked stores in dropdown menu
    if ((selected || checkedMultiStoreIds.length < 5) && enableMultiStore && onMultiStoreSelectionChange) {
      onMultiStoreSelectionChange({ multiStoreIds: checkedMultiStoreIds, onDisplay });
    }
    setMultiStoreIds(checkedMultiStoreIds);
  };

  const generateRefinementLabel = () => {
    const displayLabel = refinement?.label?.toUpperCase() === 'PICK UP TODAY'
      ? 'In Stock at Store Today'
      : refinement?.label;

    return (
      <>
        {/* SEO-friendly hidden link - Required for SEO significance. Reach out to SEO team if wanting to change */}
        <a
          href={refinement.url}
          className="sui-hidden"
          aria-hidden="true"
          tabIndex="-1"
        >
          {/* H3 element required for SEO significance. Reach out to SEO team if wanting to change */}
          <h3>{displayLabel}</h3>
        </a>

        {displayLabel}
        { isRefinementSelected && (
          <span className="sui-absolute sui-mr-2 sui-mt-2px sui-right-0">
            <ConfirmedFilled size="small" />
          </span>
        )}
      </>
    );
  };

  const generateIcon = () => {
    const { refinementKey } = refinement || null;
    // Pick Up Today : 1z175a5
    // Free 2-Day Delivery : bwo6i
    // Same-Day Delivery : 1z175cq
    if (refinementKey === '1z175a5' && refinement.label === 'In Stock at Store Today') {
      return <InStorePickup color="primary" />;
    }
    if (refinementKey === '1z175a5' && refinement.label !== 'In Stock at Store Today') {
      return <Store color="primary" />;
    }
    if (refinementKey === 'bwo6i') {
      return <Delivery color="primary" />;
    }
    if (refinementKey === '1z175cq') {
      return <DeliveryExpress color="primary" />;
    }
    return null;
  };

  const labelClassName = classNames('refinement__link', {
    'refinement__link--special-value': refinement.label === 'Special Values'
  });

  const toggleSwitchClasses = classNames(
    'refinement__toggle-switch',
    'refinement__toggle-switch--no-padding',
    {
      'refinement__toggle-switch--padding-right': multiFacet
    });

  if (
    isApplianceBundleRef.current
      && (refinement.label === 'Same-Day Delivery' || refinement.label === 'Next-Day Delivery')
  ) {
    return null;
  }

  const baseClasses = [
    'sui-lab-btn-base',
    'sui-bg-primary',
    'sui-border-solid',
    'sui-border-1',
    'sui-rounded-base',
    'sui-text-primary',
    'sui-border-1',
    'sui-p-[7px]',
    'sui-px-2',
    'sui-border-input-primary',
    'sui-flex-grow',
    'sui-mb-1'
  ];

  const hoverClasses = !isRefinementSelected ? [
    'hover:sui-outline',
    'hover:sui-outline-2',
    'hover:-sui-outline-offset-2',
    'hover:sui-outline-button-hover-accent',
    'focus-visible:sui-outline',
    'focus-visible:sui-outline-input-focus',
    'focus-visible:sui-outline-4',
    'focus-visible:-sui-outline-offset-4'
  ] : [];

  const tileClassNames = classNames([
    ...baseClasses,
    ...hoverClasses
  ].join(' '));

  const selectedClassNames = [
    'sui-border-2',
    'sui-border-solid',
    'sui-border-strongest',
    'hover:sui-bg-subtle'
  ].join(' ');

  return (
    <>
      {!toggleSwitch && (
        <Tile
          grow
          value={refinement.label}
          aria-label={refinement.label}
          disabled={parseInt(refinement.recordCount, 10) === 0}
          className={classNames(
            tileClassNames,
            { [selectedClassNames]: isRefinementSelected }
          )}
          id={`${id}-${parentID}`}
          key={`${id}-${parentID}`}
          onChange={handleOnChange}
          data-refinement={refinement.label}
          data-refinementvalue={refinement.label}
        >
          <TileContent grow alignItems="start">
            <div className="sui-contents">
              {generateIcon()}
            </div>
            <TileText>
              <div className="sui-py-1" data-testid="multi-select-refinement-secondary-drawer">
                {generateRefinementLabel()}
              </div>
            </TileText>
          </TileContent>
        </Tile>
      )}
      {toggleSwitch && (
        <ToggleSwitch
          className={toggleSwitchClasses}
          id={id}
          key={id}
          onChange={handleOnChange}
          checked={isChecked}
        >
          <div className={labelClassName}>{refinement.label}</div>
        </ToggleSwitch>
      )}
      {showStoreSelector && !isMobile && (
        <div className="refinement__store-selector">
          {isLocalized
            ? (
              <StoreSelector
                multiStoreCallback={multiStoreCallback}
                multiStoreSelection={multiStoreSelection}
                multiFacet={multiFacet}
                storeName={storeName}
              />
            )
            : <StoreSelectorPlaceholder />}
        </div>
      )}
    </>
  );
};

StandardRefinement.propTypes = {
  dimension: PropTypes.shape({}),
  id: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  enableMultiStore: PropTypes.bool,
  multiStoreSelection: PropTypes.arrayOf(PropTypes.string),
  onMultiStoreSelectionChange: PropTypes.func,
  refinement: PropTypes.shape({
    label: PropTypes.string,
    recordCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    refinementKey: PropTypes.string,
    selected: PropTypes.bool,
    url: PropTypes.string
  }),
  toggleSwitch: PropTypes.bool,
  onDisplay: PropTypes.string,
  multiFacet: PropTypes.bool,
  isRefinementSelected: PropTypes.bool,
  parentID: PropTypes.string
};

StandardRefinement.defaultProps = {
  dimension: null,
  id: null,
  enableMultiStore: false,
  multiStoreSelection: null,
  onMultiStoreSelectionChange: null,
  refinement: null,
  toggleSwitch: false,
  onDisplay: 'false',
  multiFacet: false,
  isRefinementSelected: false,
  parentID: ''
};

StandardRefinement.displayName = 'StandardRefinement';

StandardRefinement.dataModel = extend(StoreSelector);

export { StandardRefinement };
